import { supabase } from '../config/supabaseClient';
import { API_BASE_URL } from '../config';

export interface TrackingDomain {
  id: string;
  user_id: string;
  domain: string;
  cname_target: string;
  provider: string | null;
  verified: boolean;
  verified_at: string | null;
  ssl_status: string | null;
  last_health: string | null;
  created_at: string;
  updated_at: string;
}

const buildApiUrl = (path: string) => {
  const cleanPath = path.trim();
  const formattedPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
  const cleanBaseUrl = API_BASE_URL.trim();
  return `${cleanBaseUrl}${formattedPath}`;
};

const getToken = async () => {
  const sessionResp = await supabase.auth.getSession();
  const data = (sessionResp as any)?.data || {};
  return data.session?.access_token || '';
};

const fetchJSON = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
  const token = await getToken();
  const method = options.method || 'GET';

  const userResp = await supabase.auth.getUser();
  const userData = (userResp as any)?.data;
  const userId = userData?.user?.id;
  if (!userId) throw new Error('User authentication required');

  let finalUrl = url;
  let body;
  if (method === 'GET') {
    finalUrl = finalUrl + (finalUrl.includes('?') ? '&' : '?') + `userId=${encodeURIComponent(userId)}`;
  } else if (options.body) {
    body = JSON.stringify({ ...JSON.parse(options.body as string), userId });
  } else {
    body = JSON.stringify({ userId });
  }

  const res = await fetch(finalUrl, {
    ...options,
    method,
    headers: {
      ...(options.headers || {}),
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    ...(method !== 'GET' ? { body } : {}),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
};

const trackingDomainsService = {
  async list(): Promise<{ domains: TrackingDomain[] }> {
    return fetchJSON(buildApiUrl('/api/email/tracking-domains'));
  },

  async create(domain: string): Promise<TrackingDomain> {
    if (!domain) throw new Error('domain is required');
    return fetchJSON(buildApiUrl('/api/email/tracking-domains'), {
      method: 'POST',
      body: JSON.stringify({ domain })
    });
  },

  async remove(id: string): Promise<void> {
    if (!id) throw new Error('id is required');
    await fetchJSON(buildApiUrl(`/api/email/tracking-domains/${encodeURIComponent(id)}`), {
      method: 'DELETE'
    });
  },

  async verify(id: string): Promise<TrackingDomain> {
    if (!id) throw new Error('id is required');
    return fetchJSON(buildApiUrl(`/api/email/tracking-domains/${encodeURIComponent(id)}/verify`), {
      method: 'POST'
    });
  },

  async health(id: string): Promise<{ status: string; https_ok: boolean }> {
    if (!id) throw new Error('id is required');
    return fetchJSON(buildApiUrl(`/api/email/tracking-domains/${encodeURIComponent(id)}/health`));
  }
};

export default trackingDomainsService;
